import { useState, useRef, useEffect } from 'react';

export const currencies = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
];

interface CurrencySelectorProps {
  value?: string;
  onChange?: (currency: string) => void;
  alignLeft?: boolean;
}

export const CURRENCY_STORAGE_KEY = 'traderhub24_selected_currency';

function CurrencySelector({ value, onChange, alignLeft }: CurrencySelectorProps = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Get initial currency from localStorage or prop or default
  const getInitialCurrency = () => {
    if (value) {
      return currencies.find(c => c.code === value) || currencies[0];
    }
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (stored) {
      const currency = currencies.find(c => c.code === stored);
      if (currency) return currency;
    }
    return currencies[0];
  };

  const [selectedCurrency, setSelectedCurrency] = useState(getInitialCurrency);

  // Update selected currency when value prop changes
  useEffect(() => {
    if (value) {
      const currency = currencies.find(c => c.code === value);
      if (currency) {
        setSelectedCurrency(currency);
        localStorage.setItem(CURRENCY_STORAGE_KEY, currency.code);
      }
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleCurrencyChange = (currency: typeof currencies[0]) => {
    setSelectedCurrency(currency);
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency.code);
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('currencyChanged'));
    setIsOpen(false);
    if (onChange) {
      onChange(currency.code);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        aria-label="Select currency"
      >
        <span className="text-lg">{selectedCurrency.symbol}</span>
        <span className="hidden sm:inline">{selectedCurrency.code}</span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={`absolute bottom-full mb-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden ${alignLeft ? 'left-0' : 'right-0'}`}>
          <div className="py-1">
            {currencies.map((currency) => (
              <button
                key={currency.code}
                onClick={() => handleCurrencyChange(currency)}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                  selectedCurrency.code === currency.code
                    ? 'bg-orange-50 text-orange-600 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="text-lg">{currency.symbol}</span>
                <div className="flex flex-col items-start">
                  <span>{currency.code}</span>
                  <span className="text-xs text-gray-500">{currency.name}</span>
                </div>
                {selectedCurrency.code === currency.code && (
                  <svg
                    className="w-4 h-4 ml-auto text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CurrencySelector;
